import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useContractTemplatesStore } from '~/stores/contractTemplates';
import type { ContractTemplate } from '~/types/models';

export function useContractTemplateSync(options: {
    open: () => boolean;
    enabled: () => boolean;
    templateId: () => string;
    onRefresh: (template: ContractTemplate) => void;
    onError: (error: unknown) => void;
}) {
    const store = useContractTemplatesStore();
    const loading = ref(false);
    let manualRefresh = false;
    let disposed = false;
    let observed: { id: string; fingerprint: string } | undefined;
    const selected = computed(() => store.templates.find(template => template.id === options.templateId()));

    async function reloadTemplates() {
        if (loading.value) return false;
        loading.value = true;
        try { await store.fetchAll(); return true; }
        catch (error) { options.onError(error); return false; }
        finally { loading.value = false; }
    }

    async function refreshTemplate() {
        if (!options.open() || !options.enabled() || !options.templateId() || loading.value) return;
        manualRefresh = true;
        try {
            if (await reloadTemplates() && !disposed && options.open() && options.enabled() && selected.value) {
                options.onRefresh(selected.value);
            }
        } finally { manualRefresh = false; }
    }

    watch(() => ({
        id: options.templateId(),
        fingerprint: selected.value ? JSON.stringify([selected.value.title, selected.value.contentHtml, selected.value.fields, selected.value.tables || []]) : '',
        open: options.open(),
        enabled: options.enabled(),
    }), next => {
        if (!next.id) { observed = undefined; return; }
        if (!next.fingerprint) return;
        if (observed?.id !== next.id) { observed = next; return; }
        if (next.fingerprint !== observed.fingerprint && next.open && next.enabled) {
            observed = next;
            if (!manualRefresh && selected.value) options.onRefresh(selected.value);
        }
    }, { immediate: true });

    watch(options.open, open => { if (open) void reloadTemplates(); }, { immediate: true });
    function onFocus() {
        if (options.open() && options.enabled() && options.templateId()) void reloadTemplates();
    }
    function onVisibilityChange() { if (document.visibilityState === 'visible') onFocus(); }
    onMounted(() => {
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibilityChange);
    });
    onBeforeUnmount(() => {
        disposed = true;
        window.removeEventListener('focus', onFocus);
        document.removeEventListener('visibilitychange', onVisibilityChange);
    });
    return { loading, refreshTemplate };
}
